/*
 * Lightweight, dependency-free toast notifications.
 *
 * Drop-in replacement for the old react-toastify surface: the public API
 * (`toast.error/success/info/warn`, `toast.POSITION`, and the `{ position,
 * autoClose, toastId }` option bag) matches what the call sites already use,
 * so switching a file over is just a change of import path.
 *
 * The UI lives in components/notifications/Toaster.jsx, which subscribes to
 * the events emitted here.
 */

import i18n from '../i18n/i18n';

const listeners = new Set();
let counter = 0;

export const POSITION = {
    TOP_LEFT: 'top-left',
    TOP_RIGHT: 'top-right',
    TOP_CENTER: 'top-center',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_RIGHT: 'bottom-right',
    BOTTOM_CENTER: 'bottom-center',
};

const DEFAULT_DURATION = 4500;

const emit = (action) => listeners.forEach((l) => l(action));

// Called by the <Toaster/> to receive add / remove / clear events.
export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const toMessage = (value) => {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    if (value instanceof Error) return value.message;
    // API errors sometimes arrive as { message } or a plain object.
    if (typeof value === 'object') return value.message || i18n.t('toaster.error');
    return String(value);
};

const notify = (type, message, opts = {}) => {
    const text = toMessage(message);
    // Some success paths call through with an empty message — nothing to show.
    if (!text) return null;

    const id = opts.toastId != null ? opts.toastId : ++counter;
    const duration =
        opts.autoClose === false
            ? null
            : typeof opts.autoClose === 'number'
                ? opts.autoClose
                : DEFAULT_DURATION;

    emit({
        kind: 'add',
        toast: {
            id,
            type,
            message: text,
            title: opts.title,
            position: opts.position || POSITION.BOTTOM_RIGHT,
            duration,
        },
    });
    return id;
};

export const toast = (message, opts) => notify('default', message, opts);
toast.success = (message, opts) => notify('success', message, opts);
toast.error = (message, opts) => notify('error', message, opts);
toast.info = (message, opts) => notify('info', message, opts);
toast.warn = (message, opts) => notify('warn', message, opts);
toast.warning = toast.warn;
toast.dismiss = (id) => emit(id == null ? { kind: 'clear' } : { kind: 'remove', id });
toast.POSITION = POSITION;

export default toast;
